import React, { useRef, useState } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { Form } from "@unform/web";
import { FormHandles } from "@unform/core";
import { BsArrowLeftSquare } from "react-icons/bs";
import { IItbi } from "../../../../../shared/providers/itbi/interfaces";
import { Input, MainButton, Select } from "../../../../global";
import { ApiErrorHandler } from "../../../../../shared/utils/errors.utils";
import { showToast } from "../../../../global/toast";
import { dateMask } from "../../../../../shared/utils/mask.utils";
import { CurrencyInputString } from "../../../../global/currency-input/_index";
import { ItbiApiService } from "../../../../../shared/services/api/itbi-api.service";
import { itbiValidator } from "./components/validator";
import { yupValidationErrorByField } from "../../../../../shared/utils/yup-error-by-field";

interface Props {
    register: string;
    data?: IItbi;
    backButton?(): void;
    doAfterSubmit(subscription: string): void;
}

export const ItbiEdit: React.FC<Props> = ({
    register,
    data,
    backButton,
    doAfterSubmit,
}) => {
    const formRef = useRef<FormHandles>(null);
    const [loading, setLoading] = useState<boolean>(false);

    const handleSubmitEdit = async (formData: {
        idForm: number;
        dateForm: string;
        valueForm: string;
        financingValueForm: string;
        baseForm: string;
        financingBaseForm: string;
        complementForm: string;
        obsForm: string;
        typeForm: string;
        sectorForm: string;
        quadraForm: string;
        loteForm: string;
    }) => {
        const payload: IItbi = {
            id: data?.id ?? undefined,
            exercicio: "2025",
            tipocadastro: "1",
            cadastrogeral: register,
            datalancamento:
                data?.datalancamento || new Date().toISOString().split("T")[0],
            valor: formData.valueForm || "",
            valorfinanciado: formData.financingValueForm || "",
            basecalculo: formData.baseForm || "",
            basecalculofinanciado: formData.financingBaseForm || "",
            complemento: formData.complementForm || "",
            observacao: formData.obsForm || "",
            tipoimovel: formData.typeForm || "",
            setor: formData.sectorForm || "",
            quadra: formData.quadraForm || "",
            lote: formData.loteForm || "",
        };

        try {
            setLoading(true);

            await itbiValidator(payload);

            await ItbiApiService.createItbi(payload);

            showToast({
                type: "success",
                message: "Dados do ITBI (2025) atualizados com sucesso",
            });

            doAfterSubmit(register);
        } catch (err) {
            ApiErrorHandler(err);
            const validationErrorByField = yupValidationErrorByField(err);

            formRef.current?.setErrors(validationErrorByField);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="bg-gray-50">
            {backButton && (
                <div className="grid grid-cols-3 grid-rows-1">
                    <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip>VOLTAR</Tooltip>}
                    >
                        <div className="mb-3 flex space-x-2 justify-center">
                            <button type="button" onClick={backButton}>
                                <BsArrowLeftSquare color="teal" size={20} />
                            </button>
                        </div>
                    </OverlayTrigger>
                </div>
            )}
            <p>Adicionar ou Editar ITBI (2025)</p>
            {!loading && (
                <Form
                    onSubmit={handleSubmitEdit}
                    ref={formRef}
                    className="w-full md:w-6/6 relative"
                >
                    <div className="grid gap-1 grid-cols-2 grid-rows-1">
                        <Input
                            name="dateForm"
                            label="Data Lançamento"
                            disabled
                            hidden
                            defaultValue={dateMask(
                                data?.datalancamento ||
                                    new Date().toISOString().split("T")[0],
                                "date"
                            )}
                        />
                        <CurrencyInputString
                            name="valueForm"
                            label="Valor"
                            prefix="R$ - "
                            placeholder="1.000,00"
                            defaultValue={data?.valor || ""}
                        />
                        <CurrencyInputString
                            name="financingValueForm"
                            label="Valor (Financiado)"
                            prefix="R$ - "
                            placeholder="1.500,00"
                            defaultValue={data?.valorfinanciado || ""}
                        />
                        <CurrencyInputString
                            name="baseForm"
                            label="Base de cálculo"
                            prefix="R$ - "
                            placeholder="70.000,00"
                            defaultValue={data?.basecalculo || ""}
                        />
                        <CurrencyInputString
                            name="financingBaseForm"
                            label="Base de cálculo (Financiado)"
                            prefix="R$ - "
                            placeholder="300.000,00"
                            defaultValue={data?.basecalculofinanciado || ""}
                        />
                        <Input
                            name="complementForm"
                            label="Complemento"
                            placeholder="Ex: BLOCO A - UNIDADE 1 - FUNDOS - CONDOMÍNIO"
                            defaultValue={data?.complemento || ""}
                        />
                        <Select
                            name="typeForm"
                            label="Tipo do imóvel"
                            placeholder="Selecione entre Unidade e Terreno"
                            defaultValue={
                                data?.tipoimovel
                                    ? {
                                          label: data.tipoimovel,
                                          value: data.tipoimovel,
                                      }
                                    : undefined
                            }
                            options={[
                                { label: "Unidade", value: "P" },
                                { label: "Terreno", value: "T" },
                            ]}
                            isClearable
                        />
                    </div>

                    <Input
                        name="obsForm"
                        label="Observação"
                        placeholder="EX: MATRÍCULA - PROTOCOLO - PARTE IDEAL"
                        defaultValue={data?.observacao || ""}
                    />

                    <div className="grid grid-cols-3 grid-rows-1">
                        <Input
                            name="sectorForm"
                            label="Setor"
                            placeholder="Ex: 01"
                            defaultValue={data?.setor || ""}
                        />
                        <Input
                            name="quadraForm"
                            label="Quadra"
                            placeholder="Ex: 001"
                            defaultValue={data?.quadra || ""}
                        />
                        <Input
                            name="loteForm"
                            label="Lote"
                            placeholder="Ex: 0001"
                            defaultValue={data?.lote || ""}
                        />
                    </div>
                    <div className="md:w-1/3 h-10 mt-2">
                        <MainButton type="submit">SALVAR</MainButton>
                    </div>
                </Form>
            )}
        </div>
    );
};
